import * as api from '../../api';
import { installConnector } from '../company/Services';
export { loadTimezones } from '../company/Services';

const SERVICES_URL = `/api/services`;

const serviceUrl = (uid, type, action) =>
    `${SERVICES_URL}/${uid}/${type}/${action}`;

export const loadServices = () => checkResponse(api.get(SERVICES_URL));

export const installService = (uuid, identifier) =>
    installConnector(uuid, identifier);

const serviceApiFactory = type => (action, service = {}, payload = {}) => {
    const url = serviceUrl(service.uuid, type, action);
    return checkResponse(
        api.post(url, {
            ...payload,
        })
    );
};

const checkResponse = request =>
    request.then(response => {
        if (response.error) {
            throw new Error(response.error);
        }
        if (typeof response === 'string') {
            throw new Error('Incorrect reponse format');
        }
        return typeof response.data !== 'undefined' ? response.data : response;
    });

export const createDefaultService = (() => {
    const VERIFY_SUCCESS = 'success';

    const prepareLoadedProducts = products =>
        products.map(data => ({
            internal: {
                id: data.subscription_level.identifier,
                name: data.subscription_level.name,
            },
            external: {
                id: data.external_product.identifier,
                name: data.external_product.name,
            },
        }));
    const prepareUpdateProducts = products =>
        products.map(product => ({
            subscription_level: {
                identifier: product.internal.id,
                name: product.internal.name,
            },
            external_product: {
                identifier: product.external.id,
                name: product.external.name,
            },
        }));

    const checkSuccess = async request => {
        try {
            const { status } = await request;
            if (status) {
                if (status === VERIFY_SUCCESS) {
                    return true;
                }
            }
        } catch (e) {
            console.warn(e);
        }
        return false;
    };
    const arrayToDict = (data, key = 'id') =>
        data.reduce((acc, { [key]: id, name }) => {
            acc[id] = name;
            return acc;
        }, {});

    return serviceIdentifier => {
        const serivceApi = serviceApiFactory(serviceIdentifier);

        return {
            load(service) {
                return serivceApi('load', service);
            },
            verify(service, payload) {
                return checkSuccess(serivceApi('verify', service, payload));
            },
            update(service, payload) {
                return checkSuccess(serivceApi('update', service, payload));
            },
            remove(service) {
                return checkSuccess(serivceApi('remove', service));
            },
            synchronize(service) {
                return serivceApi('synchronize', service);
            },
            async products(service, payload) {
                const products = await serivceApi('products', service, payload);
                return prepareLoadedProducts(products);
            },
            productsLookups(service, payload) {
                return serivceApi('products_lookups', service, payload);
            },
            updateProducts(service, products) {
                const mapping = prepareUpdateProducts(products);
                return serivceApi('update_products', service, {
                    mapping,
                });
            },
            companyPrimaryContact(service, companyId) {
                return serivceApi('company_primary_contact', service, {
                    external_organization_identifier: companyId,
                });
            },
            async companies(service) {
                const lookups = {
                    external_agreement: {},
                    external_company: {},
                };
                const { internal, mapping } = await serivceApi(
                    'companies',
                    service
                );

                const mappingMap = mapping.reduce((acc, item) => {
                    acc[item.organization.identifier] = {
                        agreement: item.external_agreement,
                        company: item.external_organization,
                        status: item.status,
                        last_sync: item.last_sync,
                        message: item.message,
                    };
                    const aid = item.external_agreement.identifier;
                    const aname = item.external_agreement.name;
                    const cid = item.external_organization.identifier;
                    const cname = item.external_organization.name;
                    lookups.external_agreement[aid] = aname;
                    lookups.external_company[cid] = cname;
                    return acc;
                }, {});

                const data = internal.map(company => {
                    const item = mappingMap[company.uuid] || {
                        agreement: {},
                        company: {},
                        status: '',
                        message: '',
                        last_sync: '',
                    };
                    return {
                        company_uuid: company.uuid,
                        company_name: company.name,
                        external_company: item.company.identifier || '',
                        external_agreement: item.agreement.identifier || '',
                        status: item.status,
                        last_sync: item.last_sync,
                        message: item.message,
                    };
                });

                return {
                    data,
                    lookups,
                };
            },
            async companiesExternal(service, search = '') {
                const data = await serivceApi('companies_external', service, {
                    search,
                });
                return arrayToDict(data);
            },
            async companiesExternalAgreements(service, id, search = '') {
                const data = await serivceApi(
                    'companies_external_agreements',
                    service,
                    {
                        id,
                        search,
                    }
                );
                return arrayToDict(data);
            },
            updateCompanies(service, data, lookups) {
                const isFilled = ({ external_agreement, external_company }) =>
                    external_agreement && external_company;
                const mapping = data.filter(isFilled).map(row => {
                    const {
                        external_company,
                        external_agreement,
                        company_uuid,
                        company_name,
                    } = row;
                    return {
                        organization: {
                            identifier: company_uuid,
                            name: company_name,
                        },
                        external_organization: {
                            identifier: external_company,
                            name: lookups.external_company[external_company],
                        },
                        external_agreement: {
                            identifier: external_agreement,
                            name:
                                lookups.external_agreement[external_agreement],
                        },
                    };
                });
                return serivceApi('update_companies', service, {
                    mapping,
                });
            },
        };
    };
})();
